import { ipToInt } from "./ip";

export const calculateHostIP = (cidr: string, hostOffset: number): string => {
  const cidrRegex =
    /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\/([0-9]|[1-2][0-9]|3[0-2])$/;
  if (!cidrRegex.test(cidr)) {
    console.error("calculateHostIP Invalid CIDR format");
    return;
  }

  const [networkIp, prefixStr] = cidr.split("/");
  const prefix = parseInt(prefixStr, 10);

  const intToIp = (num: number): string => {
    return [
      (num >>> 24) & 255,
      (num >>> 16) & 255,
      (num >>> 8) & 255,
      num & 255,
    ].join(".");
  };

  const networkAddress = ipToInt(networkIp);
  const networkMask = 0xffffffff << (32 - prefix);
  const broadcastAddress = networkAddress | (~networkMask >>> 0);

  if ((networkAddress & ~networkMask) !== 0) {
    console.error(
      "Invalid network address (host bits must be 0)",
      networkAddress & ~networkMask
    );
    return;
  }

  const totalHosts = (1 << (32 - prefix)) - 2;

  if (hostOffset < 0 || hostOffset > totalHosts + 1) {
    console.error(`Host offset must be between 0 and ${totalHosts + 1}`);
    return;
  }

  const hostIP = networkAddress + hostOffset;

  if (hostOffset === 0) {
    return intToIp(networkAddress);
  } else if (hostOffset === totalHosts + 1) {
    return intToIp(broadcastAddress);
  } else {
    return intToIp(hostIP);
  }
};

/**
 * Returns the highest usable host offset within a CIDR, i.e. the last address
 * before the broadcast (`calculateHostIP(cidr, getMaxHostOffset(cidr))` is the
 * last usable host). Returns `undefined` for an invalid CIDR. Used to allocate
 * probe IPs from the top of the CIDR downwards so they never collide with the
 * agents, which allocate from the bottom upwards.
 */
export const getMaxHostOffset = (cidr: string): number | undefined => {
  const cidrRegex =
    /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\/([0-9]|[1-2][0-9]|3[0-2])$/;
  if (!cidrRegex.test(cidr)) {
    console.error("getMaxHostOffset Invalid CIDR format");
    return;
  }
  const prefix = parseInt(cidr.split("/")[1], 10);
  // totalHosts = usable hosts; offset totalHosts+1 is the broadcast address.
  return (1 << (32 - prefix)) - 2;
};

export const isIpInCidr = (ip: string, cidr: string): boolean => {
  const ipRegex =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  if (!ipRegex.test(ip)) {
    console.error("Invalid IP address format");
    return;
  }

  const cidrRegex =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/([0-9]|[1-2][0-9]|3[0-2])$/;
  if (!cidrRegex.test(cidr)) {
    console.error("isIpInCidr Invalid CIDR format");
    return;
  }

  const [networkIp, prefixStr] = cidr.split("/");
  const prefix = parseInt(prefixStr, 10);
  const networkAddress = ipToInt(networkIp);
  const ipAddress = ipToInt(ip);

  const mask = 0xffffffff << (32 - prefix);

  return (ipAddress & mask) === (networkAddress & mask);
};

const cidrRegex =
  /^(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\/([0-9]|[1-2][0-9]|3[0-2])$/;

function isValidCidr(cidr: string): boolean {
  return cidrRegex.test(cidr);
}

export function networksDoNotOverlap(cidrA: string, cidrB: string): boolean {
  const parseCidr = (cidr: string) => {
    const [ip, prefixStr] = cidr.split("/");
    const prefix = parseInt(prefixStr, 10);
    const net = ipToInt(ip);
    const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
    const network = net & mask;
    const broadcast = network | (~mask >>> 0);
    return { network, broadcast };
  };

  if (!isValidCidr(cidrA) || !isValidCidr(cidrB)) {
    return !isValidCidr(cidrA) || !isValidCidr(cidrB);
  }

  const a = parseCidr(cidrA);
  const b = parseCidr(cidrB);

  return a.network <= b.broadcast && b.network <= a.broadcast;
}
